import React, { useCallback, useEffect, useRef, useState } from "react";

import cx from "classnames";
import player from "utils/player";
import Icon from "components/utils/icon";
import storage from "utils/data/storage";
import deviceInfo from "utils/deviceInfo";
import ReactPlayer from "react-player/youtube";
import orderBy from "lodash/orderBy";

import { cloneDeep, forEach } from "lodash";
import { EVENT_KEY, EventEmitter } from "utils/eventManager";
import layout from "utils/layout";
import bigPlayImg from "../../../assets/fonts/play-button-big.svg";

import "./index.scss";
import { isEmpty } from "utils/data/validators";

const Youtube = ({ swap, content }) => {
  const [playYoutube, setPlayYoutube] = useState([]);
  const [onPause, setOnPause] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);
  const refPlayer = useRef([]);
  const [isFirstPlay, setFirstPlay] = useState([]);
  const [miniSize, setMiniSize] = useState(false);

  const isMobile = deviceInfo.isMobile;
  const hasFullscreenButton = layout.hasFullscreenButton(content, swap);
  const reversedStorageVideos = [...storage.videos].reverse();

  const handleRestart = useCallback(
    ({ url, timestamp }) => {
      if (!url) return;
      const _playYoutube = cloneDeep(playYoutube);
      const _index = _playYoutube?.findIndex(
        (youtube) => youtube?.url === url && youtube?.timestamp === timestamp
      );
      if (!isEmpty(_playYoutube) && _index >= 0) {
        _playYoutube[_index].isStop = false;
      }
      setPlayYoutube(_playYoutube);
    },
    [playYoutube]
  );

  useEffect(() => {
    EventEmitter.subscribe(EVENT_KEY.TIME_UPDATE, (timeVideo) => {
      setOnPause(false);

      if (isMobile) {
        const mute =
          playYoutube?.find((item) => {
            const _startTime = item?.timestamp;
            const _endTime = item?.timestamp + item?.duration;
            const _conditionMute =
              timeVideo >= _startTime - 2 && timeVideo <= _endTime + 1;

            if (_conditionMute) return true;
            return false;
          }) || false;

        if (!mute) {
          EventEmitter.dispatch(EVENT_KEY.MUTE_VIDEO, false);
        } else {
          EventEmitter.dispatch(EVENT_KEY.MUTE_VIDEO, true);
        }
      }

      forEach(playYoutube, (item, index) => {
        const _startTime = item?.timestamp;
        const _endTime = item?.timestamp + item?.duration;
        const _conditionPlaying =
          timeVideo >= _startTime && timeVideo <= _endTime;
        const timeStampBefore = playYoutube[index - 1]?.timestamp || 1000000000;

        // Youtube nằm trong khoảng time play => playing: true
        if (
          _conditionPlaying &&
          !item?.playing &&
          timeVideo < timeStampBefore
        ) {
          const _playYoutube = cloneDeep(playYoutube);
          if (!isEmpty(_playYoutube)) {
            // Mở lại youtube đã đóng
            if (timeVideo < _startTime + 2 && _playYoutube[index].isStop) {
              _playYoutube[index].isStop = false;
            }
            _playYoutube[index].playing = true;
          }
          setPlayYoutube(_playYoutube);
          return false;
        }

        // Youtube không nằm trong khoảng time play => playing: false
        if (
          (!_conditionPlaying || timeVideo > timeStampBefore) &&
          item?.playing
        ) {
          const _playYoutube = cloneDeep(playYoutube);
          if (!isEmpty(_playYoutube)) {
            _playYoutube[index].playing = false;
          }
          setPlayYoutube(_playYoutube);
        }
      });
    });

    EventEmitter.subscribe(EVENT_KEY.PAUSE_VIDEO, () => setOnPause(true));

    EventEmitter.subscribe(EVENT_KEY.PLAYBACK_RATE, (playbackRate) => {
      setPlaybackRate(playbackRate);
    });

    EventEmitter.subscribe(EVENT_KEY.TIME_SEEK, (timeVideo) => {
      forEach(playYoutube, (item, index) => {
        const _startTime = item?.timestamp;
        const _endTime = item?.timestamp + item?.duration;
        const _conditionPlaying =
          timeVideo >= _startTime && timeVideo <= _endTime;

        // Youtube không nằm trong khoảng time play => playing: false and seek to time youtube follow time video
        if (_conditionPlaying) {
          if (isMobile) {
            EventEmitter.dispatch(EVENT_KEY.MUTE_VIDEO, true);
          }

          const _playYoutube = cloneDeep(playYoutube);
          if (!isEmpty(_playYoutube)) {
            _playYoutube[index].playing = true;
          }
          setPlayYoutube(_playYoutube);

          setTimeout(
            () => refPlayer?.current[index].seekTo(timeVideo - item.timestamp),
            1000
          );
          return false;
        }

        refPlayer?.current[index].seekTo(0);
      });
    });

    EventEmitter.subscribe(EVENT_KEY.RESTART_VIDEO, ({ url, timestamp }) =>
      handleRestart({ url, timestamp })
    );

    return () => {
      EventEmitter.unsubscribe(EVENT_KEY.TIME_UPDATE);
      EventEmitter.unsubscribe(EVENT_KEY.TIME_SEEK);
      EventEmitter.unsubscribe(EVENT_KEY.PAUSE_VIDEO);
      EventEmitter.unsubscribe(EVENT_KEY.RESTART_VIDEO);
    };
  }, [handleRestart, isMobile, playYoutube]);

  const handleDuration = useCallback(
    ({ duration, item }) => {
      if (reversedStorageVideos?.length - 1 === playYoutube?.length) {
        const _playYoutube = [
          ...playYoutube,
          { ...item, duration, playing: false, isStop: false },
        ];
        const _PlayYoutubeDesc = orderBy(_playYoutube, ["timestamp"], ["desc"]);
        return setPlayYoutube(_PlayYoutubeDesc);
      }
      setPlayYoutube((prev) => {
        return [...prev, { ...item, duration, playing: false, isStop: false }];
      });
    },
    [playYoutube, reversedStorageVideos?.length]
  );

  const handleOnStart = useCallback(
    ({ item, index }) => {
      if (!isMobile) return;

      setFirstPlay((prev) => [...prev, { ...item }]);
      setTimeout(
        () =>
          refPlayer?.current[index].seekTo(
            player?.primary?.currentTime() -
              reversedStorageVideos[index]?.timestamp
          ),
        1000
      );
    },
    [isMobile, reversedStorageVideos]
  );

  const handleStop = useCallback(
    (item) => {
      if (!item?.url) return;

      const _playYoutube = cloneDeep(playYoutube);
      const _index = _playYoutube?.findIndex(
        (youtube) =>
          youtube?.url === item?.url && youtube?.timestamp === item?.timestamp
      );
      if (!isEmpty(_playYoutube) && _index >= 0) {
        _playYoutube[_index].isStop = true;
      }
      setPlayYoutube(_playYoutube);
    },
    [playYoutube]
  );

  const renderYoutube = useCallback(() => {
    return reversedStorageVideos.map((item, index) => {
      const { url, timestamp } = item;
      const isThisYoutube = playYoutube?.find(
        (item) => item?.url === url && item?.timestamp === timestamp
      );
      const playing = isThisYoutube?.playing || false;
      const isStop = isThisYoutube?.isStop || false;
      const firstPlay =
        isFirstPlay.find((config) => config?.url === url) || false;

      return (
        <div
          className={cx({
            "youtube-share": true,
            "youtube-share__active": playing && !isStop,
          })}
          key={`${url}--${index}`}
        >
          <div
            className={cx({
              "react-player-wrap": true,
              "mini-size": miniSize,
            })}
          >
            <ReactPlayer
              playing={
                isMobile && !firstPlay ? false : playing && !onPause && !isStop
              }
              ref={(el) => (refPlayer.current[index] = el)}
              playsinline
              url={url}
              onDuration={(duration) => handleDuration({ duration, item })}
              className="react-player"
              controls={false}
              onStart={() => handleOnStart({ item, index })}
              playbackRate={playbackRate}
            />

            <div
              className="btn-size"
              onClick={() => setMiniSize((prev) => !prev)}
              onTouchStart={() => setMiniSize((prev) => !prev)}
            >
              <Icon name={miniSize ? "maximize" : "minimize"} size={24} />
            </div>

            <div
              className="react-player__stop"
              onClick={() => handleStop(isThisYoutube)}
              onTouchStart={() => handleStop(isThisYoutube)}
              style={
                miniSize || !hasFullscreenButton
                  ? {
                      top: 0,
                      right: 0,
                      width: "24px",
                      height: "24px",
                      fontSize: "14px",
                      background: "rgba(0, 0, 0, 0.4)",
                    }
                  : {}
              }
            >
              X
            </div>
          </div>

          {isMobile && !firstPlay && (
            <div
              className="react-player__firstplay"
              onClick={() => handleOnStart({ item, index })}
              onTouchStart={() => handleOnStart({ item, index })}
              style={
                miniSize
                  ? {
                      width: "120px",
                      height: "70px",
                      bottom: 0,
                    }
                  : {}
              }
            >
              <img
                src={bigPlayImg}
                alt="icon"
                width={miniSize ? 36 : 100}
                height={miniSize ? 36 : 100}
              />
              <p className="react-player__firstplay__text">Click to start</p>
            </div>
          )}

          {(!isMobile || !!firstPlay) && (
            <div className="react-player__layer" />
          )}
        </div>
      );
    });
  }, [
    handleDuration,
    handleOnStart,
    handleStop,
    hasFullscreenButton,
    isFirstPlay,
    isMobile,
    miniSize,
    onPause,
    playYoutube,
    playbackRate,
    reversedStorageVideos,
  ]);

  return <div className="youtube-wrap">{renderYoutube()}</div>;
};

export default Youtube;
